import { ITenant } from '../../management/services/azure-resource-manager.service';
import { IScheduledTask } from '../../management/models/ecommerce/scheduled-task';
import { ConfigurationModificationTypeEnum, EDesignerConfigType, IDesignerValidationError } from '../designer-config-service/designer-config.service';
import { ConfigurationTypeEnum } from '../designer-config-service/common/base-designer-config';
import { ApiConnectionTypeEnum, SettingTypeEnum, ValueTypeEnum } from '../designer-config-service/appconfig/app-config-designer';

export class ApplicationDefinitionVo {
  id: number;
  name: string;
  description?: string;
  applicationDefinitionTypeId: number;
  organizationId: number;
  uniqueIdentifier: string;
  azAppRegBackendApplicationId: string;
  azAppRegBackendTestApplicationId: string;
  organization: OrganizationVo;
  defaultApplicationGroupId: number;
}

export class ApplicationDefinitionDto {
  id: number;
  applicationDefinitionTypeId: number;
  name: string;
  description?: string;
  organizationId: number;
  organization?: OrganizationDto;
  uniqueIdentifier: string;
}

export class NewApplicationDefinitionDto {
  id: number;
  applicationDefinitionTypeId: number;
  name: string;
  description?: string;
  organizationId: number;
  uniqueIdentifier: string;
}

export class CloneApplicationDefinitionDto {
  name: string;
  description?: string;
  organizationId: number;
  uniqueIdentifier: string;
  connectionMapping: { [key: number]: number }
}

export class ApplicationGroupVo {
  id: number;
  applicationDefinitionId: number;
  applicationGroupTypeId: number;
  name: string;
  description: string;
  sourceApplicationId?: number;
}

export class NewApplicationGroupDto {
  name: string;
  applicationDefinitionId: number;
  description: string;
  sourceApplicationId?: number;
}

export class ApplicationGroupDto {
  id: number;
  applicationDefinitionId: number;
  applicationGroupTypeId: number;
  name: string;
  description: string;
  sourceApplicationId?: number;
}

export enum ApplicationGroupTypeEnum {
  Default = 1,
  ServicePack = 2
}

export class MarketPlaceApplicationOrganization {
  organizationId?: number;
  marketPlaceApplicationId: number;
  marketPlaceApplication?: MarketPlaceApplication;
}

export class Addon {
  marketPlaceApplicationId: number;
  organizationId: number;
  name: string;
  tenantId: string;
  createdDate: string;
  included: boolean;
  tenant?: ITenant;
  applicationDefinitionId?: number;
}

export class ApplicationStatus {
  id: number;
  name: string;
}

export enum ApplicationStatusEnum {
  Main = 1,
  Published = 3,
  CommittedFeature = 4,
  Feature = 5
}

export class ApplicationDto {
  id: number;
  referenceName: string;
  applicationDefinitionId: number;
  applicationGroupId: number;
  applicationStatusId: number;
  description: string;
  createdDate: string;
  modifiedDate: string;
  commitTitle?: string;
  commitDescription?: string;
  commitDate?: string;
  userId?: number;
  isLatest?: boolean;
  applicationGroup?: ApplicationGroupDto;
  applicationDefinition?: ApplicationDefinitionDto;
  author?: UserDto;
}

export class ApplicationCanBeDeletedDto {
  reasons: string[];
  canDelete: boolean;
}

export class ApplicationVo {
  id: number;
  applicationStatusId: number;
  description: string;
  createdDate?: string;
  commitTitle?: string;
  commitDescription?: string;
  commitDate?: string;

  authorDisplayName: string;
  authorExternalId: string;

  marketplaceApplicationId?: number;
  marketplaceApplicationVersionId?: number;
  isReady: boolean;
  isLatest: boolean;
  versionName?: string;
  releaseNotes?: string;
  releaseDate?: string;

  buildStatus?: EBuildStatus;
  buildWebUrl?: string;
  buildStartTime?: string;
  buildFinishTime?: string;

  applicationGroupId?: number;
  applicationGroupName?: string;

  duration?: string;
  buildStatusDisplayText?: string;
}

export class ApplicationDefinitionType {
  id: number;
  name: string;
}

export class Locks {
  configs: LockedConfiguration[];
}

export class LockedConfiguration {
  id: number;
  applicationId: number;
  configurationTypeId: ConfigurationTypeEnum;
  referenceName: string;
  lockedBy: UserDto;
  modifiedDate: string;
}

export class UpstreamChanges {
  configs: ConfigurationChanges[];
  applicationId: number;
  baseApplicationId: number;
}

export class CommitChanges {
  configs: ConfigurationChanges[];
}

export class CommitDetails {
  transferredUncommittedChanges: boolean;
  newBranchId?: number;
}

export class Commit {
  title: string;
  description: string;
  configs: string[];
}

export class Pull {
  configs: string[];
}

export class ConfigurationChanges {
  id: number;
  baseConfigId: number;
  referenceName: string;
  configurationTypeId: EDesignerConfigType;
  modificationTypeId: ConfigurationModificationTypeEnum;
  applicationId: number;
  baseApplicationId: number;
}

export class ApplicationHistoryItem {
  commitTitle: string;
  commitMessage: string;
  commitDate: string;
  author: UserDto;
  applicationId: number;
}

export class ConfigurationHistoryItem extends ApplicationHistoryItem {
  configId: number;
  modificationTypeId: ConfigurationModificationTypeEnum;
  configType: EDesignerConfigType;
}

export class RoleType {
  id: number;
  name: string;
}

export enum RoleTypeEnum {
  Role = 1,
  DataAccessGroup = 2
}

export class OrganizationRole {
  id: number;
  organizationId: number;
  referenceName: string;
  description: string;
  organization: OrganizationVo;
  roleTypeId: RoleTypeEnum;
}

export class OrganizationRoleUser {
  roleId: number;
  userId: number;
  displayName: string;
  userPrincipalName: string;
}

export class OrganizationRoleGroup {
  roleId: number;
  groupId: number;
  name: string;
}

export class OrganizationRoleServicePrincipal {
  roleId: number;
  servicePrincipalId: number;
  name: string;
}

export class OrganizationRoleApplication {
  roleId: number;
  applicationId: number;
  application: ApplicationDto;
}

export class MarketPlaceApplicationVersionUpdate {
  fromMarketPlaceApplicationVersionId: number;
  toMarketPlaceApplicationVersionId?: number;
}

export class ApplicationReference {
  applicationId: number;
  marketPlaceApplicationVersionId: number;

  application?: ApplicationDto;
  marketPlaceApplicationVersion?: MarketPlaceApplicationVersion;
}

// NOTE: this should be removed once the ApplicationReferences start working with referenceNames
export class MarketPlaceApplicationVersion {
  marketPlaceApplicationId: number;
  applicationId?: number;
  application: ApplicationDto;
  isReady: boolean;
  isLatest: boolean;
  versionName: string;
}

export class ReferenceKeyDto {
  uniqueIdentifier: string;
  version: string;
  constructor(uniqueIdentifer: string, version: string) {
    this.uniqueIdentifier = uniqueIdentifer;
    this.version = version;
  }
}

export class MarketPlaceApplicationVersionSettingVo {
  name: string;
  description: string;
  settingType: SettingTypeEnum;
  valueType: ValueTypeEnum | null;
  value: any;
  apiConnectionType: ApiConnectionTypeEnum | null;
  apiConnectionName: string | null;
}

export class MarketPlaceApplicationVersionReferenceVo {
  uniqueIdentifier: string;
  referenceName: string;
  version: string;
  isDirect: boolean;
  settings: Array<MarketPlaceApplicationVersionSettingVo>;
}

export class MarketPlaceApplicationVersionWithSettingsVo {
  id: number;
  applicationId: number;
  marketPlaceApplicationId: number;
  referenceName: string;
  commitTitle: string;
  uniqueIdentifier: string;
  description: string;
  versionName: string;
  createdDate: string;
  isLatest: boolean;
  isApplicationLatest: boolean;
  settings: Array<MarketPlaceApplicationVersionSettingVo>;
  references: Array<MarketPlaceApplicationVersionReferenceVo>;
}

export class ApplicationReferencedFromVo {
  applicationId: number;
  applicationStatusId: number;
  commitTitle: string;
  name: string;
  description: string;
  createdDate: string;
}

export class ReferenceableModuleVo {
  marketPlaceApplicationId: number;
  referenceName: string;
  description: string;
}

export enum EBuildStatus {
  none = 'none',
  notStarted = 'notStarted',
  inProgress = 'inProgress',
  succeeded = 'succeeded',
  failed = 'failed',
  canceled = 'canceled'
}

export class OrganizationVo {
  id: number;
  tenantId: string;
  name: string;
  referenceName: string;
  description: string;
  externalEntraId: boolean;
  externalEntraIdDomainName: string;
  devOpsOrganization: string;
  homeTenantId: string;
}

export interface NewOrganizationDto {
  name: string,
  referenceName: string;
  description: string;
  tenantId: string
  homeTenantId: string
  externalEntraId: boolean;
  externalEntraIdDomainName: string;
  devOpsOrganization: string;

  userPrincipalName: string;
  userExternalId: string;
  userDisplayName: string;
}

export interface OrganizationDto {
  id: number;
  name: string,
  referenceName: string;
  description: string;
  tenantId: string
  homeTenantId: string
  externalEntraId: boolean;
  externalEntraIdDomainName: string;
  devOpsOrganization: string;
}

export enum TaskConcurrencyStrategy {
  AllowConcurrentRun = 1,
  NoConcurrencyQueueWaiting = 2,
  NoConcurrencyRemoveWaiting = 3
}

export interface IWavelengthScheduledTask extends IScheduledTask {
  id: string;
  applicationId: number;
  functionName: string;
  inputParameter: object;
  lastRunOn: Date;
  concurrencyStrategyId: TaskConcurrencyStrategy;
}

export class NewUserDto {
  email: string;
  displayName: string;
  userPrincipalName: string;
  hasManagementAccess: boolean;
  hasStudioAccess: boolean;
  externalId: string;
  redemptionUrl: string;

  organizationRoleIds: number[];
}

export class UserDto {
  id: number;
  organizationId: number;
  displayName: string;
  userPrincipalName: string;
  hasManagementAccess: boolean;
  hasStudioAccess: boolean;
  externalId: string;
}

export interface InvitationStatus {
  externalId: string;
  status: string;
}

export interface InviteUserResult {
  email: string;
  error: string | null;
}

export class NewGroupDto {
  name: string;
  externalId: string;
}
export class Group {
  id: number;
  organizationId: number;
  name: string;
  externalId: string;
}

export class ServicePrincipal {
  id: number;
  organizationId: number;
  name: string;
  externalId: string;
}

export class MarketPlaceAppType {
  id: number;
  name: string;
}

export class MarketPlaceApplication {
  id: number;
  marketPlaceApplicationTypeId: number;
  uniqueIdentifier: string;
  applicationDefinitionId: number;
  name: string;
  description: string;
  organizationId: number;
  organizationName: string;
  createdDate: Date;
  modifiedDate: Date;
}

export class MarketPlaceApplicationVersionDto {
  id: number;
  isReady: boolean;
  isLatest: boolean;
  marketPlaceApplicationId: number;
  releaseDate: string;
  releaseNotes: string;
  versionCode: string;
  versionName: string;
  containerImageUrl: string;
  applicationId?: number;
  createdDate: Date;
  modifiedDate: Date;
}

export class MarketPlaceApplicationVersionBuildDto {
  buildId: number;
  status?: EBuildStatus;
  url: string;
  startTime: string;
  finishTime: string;
}

export class MarketPlaceApplicationVersionVo {
  id: number;
  isReady: boolean;
  marketPlaceApplicationId: number;
  releaseDate: string;
  releaseNotes: string;
  versionCode: string;
  versionName: string;
  containerImageUrl: string;
  applicationId?: number;
  createdDate: Date;
  isApplicationLatest?: boolean;
}

export class Environment {
  id: number;
  organizationId: number;
  name: string;
  referenceName: string;
  description: string;
}

export class ActiveEnvironmentComponent {
  id: number;
  environment: string;
  environmentId: number;
  name: string;
  description: string;
  logo: string;
  version: string;
  websiteUrl: string;
  applicationId: number;
  applicationDefinitionId: number;
}

export class EnvironmentComponent {
  id: number;
  environmentId: number;
  organizationId: number;
  marketPlaceApplicationVersionId: number;
  marketPlaceApplicationId: number;
  marketPlaceAppTypeId: number;
  marketPlaceAppOrganizationId: number;
  name: string;
  description: string;
  applicationId: number;
  versionName: string;
  createdDate: Date;
  isActive: boolean;
}

export class EnvironmentComponentValidation {
  isValid: boolean;
  areSettingsValid: boolean;
  areAzureResourcesSet: boolean;
  isImagePresent: boolean;
  areTasksValid: boolean;
  validationErrors: IDesignerValidationError[];
}

export class EnvironmentTaskScheduler {
  hasSchedulerRunning: boolean;
  schedulerUrl: string;
  environmentId: number;
}

export class EnvironmentComponentTask {
  environmentComponentDefinitionId: number;
  environmentComponentId: number;
  environmentId: number;
  name: string;
  applicationName: string;
  moduleName: string;
  functionName: string;
  inParams: { [key: string]: any };
  cronExpression: string;
  isActive: boolean;
  isValid: boolean;
  isApplicationSource: boolean;
  concurrency: number;
  userId: string;
  createdAt: string;
  nextOccurrenceOn: Date;
  lastRunOn: Date;
  lastRunState: string;
}

export class TaskList {
  tasks: EnvironmentComponentTask[];
  totalCount: number;
}

export class UnifiedTaskList extends TaskList {
  environments: Environment[];
  taskSchedulers: EnvironmentTaskScheduler[];
  applicationDefinitions: ApplicationDefinitionVo[];
}

export class TaskHistoryPage {
  totalCount: number;
  historyRecords: TaskHistoryDetail[];
}

export class TaskProgress {
  min: number;
  max: number;
  current: number;
}

export class TaskHistoryDetail {
  runId: string;
  status: string;
  progress?: number;
  createdAt: Date;
  inParams: object;
  reason: string;
  errorDetails: string;
  outParams: object;
  duration: number;
}

export class ModifyEnvironmentComponentDefinitionDto {
  id?: number;
  environmentId: number;
  marketPlaceApplicationVersionId?: number;
  marketPlaceApplicationId?: number;
  site?: string;
  referenceName?: string;
  azSubscriptionId: string;
  azResourceGroup: string;
  azContainerApp: string;
  websiteUrl: string;
  azContainerAppJob?: string;
  azServiceBusNamespace?: string;
  azServiceBusQueue?: string;
  azServiceBusReplyNamespace?: string;
  azServiceBusReplyQueue?: string;
}

export class EnvironmentComponentSystemSettings {
  logLevel: string;
  inactivityTimeout: number | null;
  enableSecurityHeaders: boolean | null;
}

export class EnvironmentComponentDefinition {
  id: number;
  environmentId: number;
  environmentName?: string;
  organizationId?: number;
  defaultEnvironmentComponentId?: number;
  marketPlaceApplicationVersionId?: number;
  marketPlaceApplicationId?: number;
  marketPlaceAppTypeId: number;
  name?: string;
  site?: string;
  referenceName?: string;
  description?: string;
  releaseDate?: Date;
  releaseNotes?: string;
  azSubscriptionId?: string;
  azResourceGroup?: string;
  azContainerApp?: string;
  websiteUrl?: string;
  versionName?: string;
  azAppRegBackendApplicationId?: string;
  tenantId?: string;
  azContainerAppJob?: string;
  azServiceBusNamespace?: string;
  azServiceBusQueue?: string;
  azServiceBusReplyNamespace?: string;
  azServiceBusReplyQueue?: string;
}

export class EnvironmentComponentRole {
  organizationRoleId: number;
  environmentComponentId: number;
  organizationRole: OrganizationRole;
}

export class EnvironmentComponentOperationRoleAssignment {
  organizationRoleId: number;
  environmentComponentId: number;
  applicationOperationId: number;
  operationName: string;
  operationDescription: string;
  operationAppId: number;
  operationAppRefName: string;
  roleName: string;
  roleDescription: string;
}

/** Single operation entry surfaced from a referenced module's AuthorizationConfig.
 *  Returned by AuthorizationConfigurationsController.GetOperationsFromReferences and
 *  merged with the host's own config.operations[] for the assignment picker. */
export class ReferenceOperationDto {
  name: string;
  applicationReferenceName: string;
  description: string;
  isAuthorizable: boolean;
}

export class EnvironmentComponentSettingValue {
  environmentComponentId: number;
  settingTypeId: SettingTypeEnum;
  apiConnectionId?: number;
  apiConnectionType?: ApiConnectionTypeEnum;
  valueJson?: unknown;
  name: string;
  valueType?: ValueTypeEnum;
}

export interface FootprintWorkflowsBlobsDataDto {
  bundle: string;
  info: string;
}

export interface PathNode {
  name: string;
  version: string;
}

export interface CollisionPath {
  application: string;
  versionedPath: PathNode[];
}

export interface CollisionGroup {
  conflictingPackage: string;
  paths: CollisionPath[];
}

export interface DependencyCollisionInfo {
  conflictingModuleNames: string[];
  collisions: CollisionGroup[];
  title: string;
}

/** Mirrors Core/Organizations/DataObjects/MyAccessVo.cs — drives the
 *  Manager home page's "Your access" card. */
export class MyAccessVo {
  roles: MyAccessItemVo[];
  dataAccessGroups: MyAccessItemVo[];
}

export class MyAccessItemVo {
  id: number;
  referenceName: string;
  description: string;
}

/** Slim view-object for the Manager home page's "My branches" list.
 *  Mirrors Core/Applications/DataObjects/MyFeatureBranchVo.cs. */
export class MyFeatureBranchVo {
  applicationId: number;
  applicationDefinitionId: number;
  applicationDefinitionName: string;
  /** Underlying ApplicationDefinitionType id — used by the Manager home
   *  list to differentiate application branches from component packages. */
  applicationDefinitionTypeId: number;
  title: string;
  description: string;
  createdDate: string;
  modifiedDate: string;
  /** When the user last actually edited a configuration on this branch
   *  (MAX of the branch's Configuration.ModifiedDate values, falling
   *  back to the application's own ModifiedDate when no configs exist).
   *  This is what the "Active …" row label is bound to so the timestamp
   *  reflects real edit activity, not branch metadata events. */
  lastActivityDate: string;
}

export enum LicenseTypeEnum {
  concurrent = 1
}

export class NewLicenseDto {
  licenseTypeId: LicenseTypeEnum;
  marketPlaceApplicationTypeId?: number;
  seats?: number;
  expirationDate?: string;
  name?: string;
  notes?: string;
}

export class LicenseDto {
  id: number;
  guid: string;
  organizationId: number;
  licenseTypeId: LicenseTypeEnum;
  marketPlaceApplicationTypeId: number;
  seats?: number;
  expirationDate: string;
  name?: string;
  notes?: string;
}

export class LicenseVo {
  id: number;
  guid: string;
  marketPlaceApplicationTypeId: number;
  seats?: number;
  expirationDate: string;
  name?: string;
  notes?: string;
}

export class LicenseSeatVo {
  id: number;

  licenseId: number;
  licenseGuid: string;
  licenseMarketPlaceApplicationTypeId: number;

  userId?: number;
  userDisplayName: string;
  acquisitionDate?: string;
  renewalDate?: string;
  expirationDate?: string;
}

export class AllocateLicenseSeatDto {
  userId?: number;
}

